import { getGlobalData } from "../utils/api"
import Seo from "../components/elements/seo"
import Head from "next/head"
import Link from "next/link"
import { BreadcrumbJsonLd, FAQPageJsonLd, WebPageJsonLd, LocalBusinessJsonLd,  } from "next-seo"
import Image from "../components/elements/image"
//import CateringQuoteForm from "../components/catering-quote-form";

const catererLd = {
  "@context": "https://schema.org",
  "@type": "Caterer",
  "@id": "https://brownedandtoasted.com/catering#caterer",
  "name": "Browned & Toasted",
  "legalName": "Browned and Toasted LLC",
  "url": "https://brownedandtoasted.com",
  "telephone": "+1-206-778-3616",
  "image": "https://brownedandtoasted.com/assets/og-image.jpg",
  "logo": "https://brownedandtoasted.com/assets/logo.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "19551 40th Pl NE",
    "addressLocality": "Lake Forest Park",
    "addressRegion": "WA",
    "postalCode": "98155",
    "addressCountry": "US"
  },
  "areaServed": [
    "Seattle WA","Bellevue WA","Kirkland WA","Redmond WA","Lynnwood WA","Edmonds WA","Bothell WA","Shoreline WA","Woodinville WA",
    "Everett WA","Lake Forest Park WA","Western Washington","United States","Canada","Mexico"
  ],
  "serviceArea": [
    { "@type": "AdministrativeArea", "name": "Seattle–Tacoma–Bellevue, WA" },
    {
      "@type": "GeoCircle",
      "geoMidpoint": { "@type": "GeoCoordinates", "latitude": "47.6062", "longitude": "-122.3321" },
      "geoRadius": "80000"
    }
  ],
  "contactPoint": [{
    "@type": "ContactPoint",
    "contactType": "customer support",
    "telephone": "+1-206-778-3616",
    "areaServed": ["US","CA","MX"],
    "availableLanguage": ["en"]
  }]
};

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://brownedandtoasted.com/catering#service",
  "serviceType": "Dessert catering",
  "provider": { "@id": "https://brownedandtoasted.com/catering#caterer" },
  "areaServed": ["Seattle WA", "Western Washington", "United States", "Canada", "Mexico"],
  "isRelatedTo": {
    "@type": "Product",
    "name": "Marshmallow crispy bars & 2×2 minis",
    "brand": { "@type": "Brand", "name": "Browned & Toasted" }
  },
  "termsOfService": "Catering orders have no minimums. Customers cover shipping or local delivery.",
  "offers": {
    "@type": "OfferCatalog",
    "name": "Catering Options",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "2×2 Marshmallow Crispy Minis (Tray of 24)",
        "priceCurrency": "USD",
        "price": "",
        "availability": "https://schema.org/InStock",
        "url": "https://brownedandtoasted.com/catering#quote",
        "eligibleRegion": "Seattle–Tacoma–Bellevue, WA",
        "description": "Individually wrapped 2×2 minis. No minimum order. Optional custom labels.",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Custom labels", "value": "Available", "valueReference": "Branding" }
        ]
      },
      {
        "@type": "Offer",
        "name": "Full Marshmallow Crispy Bars (assorted flavors)",
        "priceCurrency": "USD",
        "price": "",
        "availability": "https://schema.org/InStock",
        "url": "https://brownedandtoasted.com/catering#quote",
        "eligibleRegion": "Seattle–Tacoma–Bellevue, WA",
        "description": "Full bars for slicing or dessert bars. No minimum order."
      },
      {
        "@type": "Offer",
        "name": "Custom Label Printing (add-on)",
        "priceCurrency": "USD",
        "price": "",
        "url": "https://brownedandtoasted.com/catering#quote",
        "description": "Add your logo or event artwork to individually wrapped minis or full bars. Typical turnaround +2–4 business days.",
        "eligibleRegion": "Seattle–Tacoma–Bellevue, WA",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "File formats", "value": "SVG, PDF, PNG (1500px+)" },
          { "@type": "PropertyValue", "name": "Proofing", "value": "Digital proof included" }
        ]
      }
    ]
  },
  "availableChannel": [
    { "@type": "ServiceChannel", "serviceUrl": "https://brownedandtoasted.com/catering#quote", "name": "Online quote form" },
    { "@type": "ServiceChannel", "telephone": "+1-206-778-3616", "name": "Phone" }
  ]
};

const webPageLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://brownedandtoasted.com/catering#webpage",
  "url": "https://brownedandtoasted.com/catering",
  "name": "Dessert Catering in Seattle | Marshmallow Crispy Bars",
  "description": "Gooey marshmallow crispy bars (2×2 minis or full bars) for weddings, corporate events & parties. No minimum orders. Western Washington delivery; US/CA/MX shipping.",
  "isPartOf": { "@id": "https://brownedandtoasted.com#website" }
};

const breadcrumbsLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://brownedandtoasted.com/" },
    { "@type": "ListItem", "position": 2, "name": "Catering", "item": "https://brownedandtoasted.com/catering" }
  ]
};

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://brownedandtoasted.com/catering#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Do you require a minimum order for catering?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Catering orders can be as small as a single tray of 24 mini 2×2 squares."
      }
    },
    {
      "@type": "Question",
      "name": "How much dessert should I order?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Plan 1.25–1.5 pieces per guest. For 100 guests, most hosts order 11–13 dozen 2×2 squares, sometimes with a few full bars for slicing."
      }
    },
    {
      "@type": "Question",
      "name": "Do you deliver and set up?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We deliver across Western Washington; customers cover shipping/delivery. We can coordinate simple table setup with your venue or planner."
      }
    },
    {
      "@type": "Question",
      "name": "Can you do custom labels?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Upload your logo or artwork with your quote request. We’ll size it to our wraps and send a quick proof. Custom labels typically add 2–4 business days."
      }
    }
  ]
};


const CateringPage = ({ global, metadata }) => {

    const metadataWithDefaults = {
      title: "Dessert Catering in Seattle | Marshmallow Crispy Bars",
      description:"Book dessert catering in Seattle. Gooey marshmallow crispy bars (2×2 minis or full bars) for weddings, corporate events & parties. No minimum orders. Custom labels available.",
      
      ...global.metadata,
        ...metadata,
    }
    const canonicalHref = metadataWithDefaults.canonicalURL
// pages/catering.js

  return (<>
        <Head>
            <link rel="canonical" href={canonicalHref} />
        </Head>
       <main>
        <Seo metadata={metadataWithDefaults}>
          <WebPageJsonLd {...webPageLd} />
          <BreadcrumbJsonLd {...breadcrumbsLd} />
          <FAQPageJsonLd {...faqLd} />
        </Seo>

      <section className="prose max-w-3xl mx-auto p-6">
        <h1>Dessert Catering in Seattle</h1>
        {/* <LocalBusinessJsonLd {...catererLd}/> */}
        <p>
          Make your dessert table easy (and ridiculously good). Our marshmallow crispy bars have more marshmallow than cereal for a soft, gooey bite.
          <strong> No minimum order</strong> for catering—start with a single tray of 24 <strong>2×2 minis</strong> or scale up for weddings and corporate events.
          We deliver across Western Washington and ship for off-site events in the U.S., Canada, and Mexico.
        </p>

        <h2>How Our Dessert Catering Works</h2>
        <ul>
          <li>2×2 minis sold in trays of 24 squares</li>
          <li>No minimum order requirement for catering</li>
          <li>Most flavors available; seasonal flavors rotate</li>
          <li>Customer pays shipping/delivery</li>
          <li>Lead time: ~2 weeks ideal for larger events</li>
          <li><strong>Optional custom labels</strong> for events and corporate branding</li>
        </ul>
        

        <h2>Popular Packages & Quantities</h2>
        <p>A quick guide—plan about <strong>1.25–1.5 pieces per guest</strong>.</p>
        <table>
          <thead>
            <tr><th>Guests</th><th>Recommended Minis</th></tr>
          </thead>
          <tbody>
            <tr><td>25</td><td>1–2 trays (24–48 squares)</td></tr>
            <tr><td>50</td><td>3 trays (72 squares)</td></tr>
            <tr><td>100</td><td>6 trays (144 squares)</td></tr>
            <tr><td>200</td><td>12 trays (288 squares)</td></tr>
          </tbody>
        </table>

        <h2>Custom Labels & Branding</h2>
        <p>
          Make it yours. We can add <strong>custom labels</strong> to individually wrapped 2×2 minis —perfect for weddings, corporate events, school spirit nights, and giveaways.
          Send your logo or artwork with your quote request and we’ll size it to our wraps and send a quick proof.
        </p>
        <ul>
          <li>Supported: PNG/SVG/PDF (vector preferred)</li>
          <li>Typical label area: 2″×2″ </li>
          <li>Turnaround: add ~2–4 business days for printing</li>
        </ul>

        <h2>Where We Cater</h2>
        <p>
          We regularly cater events in Seattle, Bellevue, Kirkland, Redmond, Everett, Tacoma, and beyond.
          Western Washington delivery is available, and shipping is an option for out-of-area events.
          Planning a local pickup or tasting? Check our <Link href="/seattle">Seattle page</Link> for pop-ups and retailers.
        </p>

        <h2>Why Marshmallow Crispy Bars?</h2>
        <p>
          Faster to serve and less messy than frosted desserts—kid- and adult-approved. They hold up well on dessert tables and can be individually wrapped for offices.
          Our recipe uses <strong>more marshmallow than cereal</strong> for a softer, gooier bite.
        </p>

        <h2 id="faq">FAQs</h2>
        
      
        <details>
          <summary>Do you require a minimum order for catering?</summary>
          <p>No. Catering orders can be as small as a single tray of 24 mini 2×2 squares. Wholesale orders have minimums due to deeper discounts.</p>
        </details>
        <details>
          <summary>Can you do custom labels?</summary>
          <p>Yes. Upload your logo/art with your quote request. We’ll size it to our wraps and send a quick proof. Custom labels can add 2–4 business days.</p>
        </details>
        <details>
          <summary>How much dessert should I order?</summary>
          <p>Plan 1.25–1.5 pieces per guest. For 100 guests, most hosts order 11–13 dozen 2×2 squares, sometimes with a few full bars for slicing.</p>
        </details>
        <details>
          <summary>Do you deliver and set up?</summary>
          <p>We deliver across Western Washington; customers cover shipping/delivery. We can coordinate simple table setup with your venue or planner.</p>
        </details>

        <h2 id="quote">Get a Catering Quote</h2>
        <p>
          Tell us your date, guest count, flavors, and if you’d like <strong>custom labels</strong>. We’ll confirm quantities and timing right away.
        </p>

        {/* Inline quote form (if available). If not, keep the CTA buttons below. */}
        {/* {typeof CateringQuoteForm === "function" ? <CateringQuoteForm /> : null} */}

        <div className="not-prose mt-4 grid sm:grid-cols-2 gap-3">
          <a className="inline-block text-center rounded-lg border px-4 py-2" href="tel:+12067783616">
            Call 206-778-3616
          </a>
          <Link className="inline-block text-center rounded-lg bg-amber-500 text-white px-4 py-2" href="/contact">
            Request a Quote
          </Link>
        </div>

        <p className="mt-6 text-sm text-gray-600">
          Looking for wholesale instead? See <Link href="/wholesale">Wholesale</Link> (first-order half-case minimums apply).
          Ready to buy now? Visit the <Link href="/store">Store</Link>.
        </p>
      </section>
    </main>
    </>
  );
}

export async function getStaticProps(context) {
    const { params, locale, locales, defaultLocale } = context
    const globalData = await getGlobalData(locale)

    const pageContext = {
        locale,
        locales,
        defaultLocale,
        //      localizations,
    }

    let metadata = {
        metaTitle: `Catering — 2×2 Marshmallow Crispy Squares (24-count) | Browned & Toasted`,
        metaDescription: `Catering with extra-gooey 2×2 marshmallow crispy squares. Priced in 24-count increments. Tray-packed or individually wrapped. Most flavors available. Ship to US, Canada, and Mexico.`,
        canonicalURL: `https://brownedandtoasted.com/catering`,
    }

    let preview = context.preview || false
    //  const localizedPaths = getLocalizedPaths(pageContext)

    // fetch the available bars
    try {
        return (
            {
                props: {
                    globalData,
                    metadata,
                    pageContext: pageContext,
                    locale,
                    preview: preview,
                },
            }
        )
    } catch (error) {
        return {
            revalidate: 60,
            props: {
                locale,
                error: 500
            }
        }
    }
};

export default CateringPage